// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { api } from '@/api/axios-instance';
import { parseResponse } from '@/api/parse-response';
import {
  FulfillmentFilter,
  FulfillmentOrder,
  fulfillmentOrderSchema,
} from '@/fulfillment/api/types';
import {
  type FulfillmentOrderPhase,
  type FulfillmentOrderSubstatus,
  PHASE_CONFIG,
  fulfillmentOrderPhaseSchema,
  fulfillmentOrderSubstatusSchema,
} from '@/fulfillment/constants/fulfillment-order-status';
import type { FulfillmentSortProperty } from '@/fulfillment/hooks/use-fulfillments';
import queryString from 'query-string';
import { z } from 'zod';

const SORT_PROPERTY_TO_BACKEND: Record<FulfillmentSortProperty, string> = {
  foNumber: 'fulfillmentOrderNumber',
  status: 'status',
  cycleTime: 'cycle_time',
};

/**
 * Type definition for the paginated response.
 */
interface PagedFulfillmentOrdersResponse {
  results: unknown[];
  totalCount: number;
}

/**
 * Type definition for the API response wrapper.
 */
interface ApiResponse {
  data: PagedFulfillmentOrdersResponse;
  metadata: { requestId: string };
}

/**
 * Type definition for the get fulfillments API response.
 */
interface FulfillmentsResponse {
  fulfillments: FulfillmentOrder[];
  resultCount: number;
}

export function getFilterStatuses(
  status?: FulfillmentOrderPhase | 'all',
  subStatuses?: FulfillmentOrderSubstatus[]
): string[] | undefined {
  const effectiveStatus = status === 'all' ? undefined : status;

  if (effectiveStatus === undefined && subStatuses === undefined) {
    return undefined;
  }

  const phases: readonly FulfillmentOrderPhase[] =
    effectiveStatus !== undefined
      ? [effectiveStatus]
      : fulfillmentOrderPhaseSchema.options;

  const allFilters = phases.flatMap((p) => PHASE_CONFIG[p].backendFilters);

  if (subStatuses === undefined || subStatuses.length === 0) {
    return allFilters;
  }

  const suffixes = new Set(subStatuses.map((m) => m.toUpperCase()));
  const allModifiers = new Set(
    fulfillmentOrderSubstatusSchema.options.map((m) => m.toUpperCase())
  );
  // Statuses whose last segment is not a known modifier (e.g. DRAFT, CANCELLED,
  // AWAITING_3PL — whose last segment '3PL' is not in allModifiers) always pass
  // through regardless of the selected sub-status filters.
  return allFilters.filter((s) => {
    const suffix = s.split('_').pop();
    if (suffix === undefined || !allModifiers.has(suffix)) {
      return true;
    }
    return suffixes.has(suffix);
  });
}

/**
 * Fetches fulfillment orders with the specified filter criteria.
 */
export async function getFulfillments(
  filter?: FulfillmentFilter
): Promise<FulfillmentsResponse> {
  const url = '/v1/fulfillment';
  const response = await api.get<ApiResponse>(url, {
    paramsSerializer: (parameters) => queryString.stringify(parameters),
    params: {
      statuses: getFilterStatuses(filter?.status, filter?.subStatuses),
      search: filter?.searchTerm,
      page: filter?.page,
      limit: filter?.pageSize,
      projectIds: filter?.projectId ? [filter.projectId] : undefined,
      fromLocationIds: filter?.fromLocationIds,
      fromCustomerIds: filter?.fromCustomerIds,
      fromLocationCompanyTypes: filter?.fromLocationCompanyTypes,
      toLocationIds: filter?.toLocationIds,
      toCustomerIds: filter?.toCustomerIds,
      toLocationCompanyTypes: filter?.toLocationCompanyTypes,
      sortBy: filter?.sort
        ? SORT_PROPERTY_TO_BACKEND[filter.sort.property]
        : undefined,
      sortOrder: filter?.sort?.direction,
    },
  });

  // Parse and validate each fulfillment order with Zod
  const fulfillmentsSchema = z.array(fulfillmentOrderSchema);
  const fulfillments = await parseResponse(
    fulfillmentsSchema,
    response.data.data.results,
    {
      schemaName: 'Fulfillments',
      endpoint: url,
    }
  );

  return {
    fulfillments,
    resultCount: response.data.data.totalCount,
  };
}
